<template>
  <div class="page-wrapper">
    <div class="form-card">
      <div class="language-switch" role="group" aria-label="Language / Jezik">
        <button type="button" :aria-pressed="activeLanguage === 'hr'" @click="setLanguage('hr')">HR</button>
        <button type="button" :aria-pressed="activeLanguage === 'en'" @click="setLanguage('en')">EN</button>
      </div>
      <h1 class="page-title">{{ $t('insuranceWithdrawal.pageTitle') }}</h1>
      <p class="explanation">{{ $t('insuranceWithdrawal.explanation') }}</p>

      <div v-if="view === 'loading'" class="state-wrapper">
        <spinner />
        <p class="state-text">{{ $t('insuranceWithdrawal.loading') }}</p>
      </div>

      <div v-else-if="view === 'notFound'" class="state-wrapper">
        <h2 class="state-title">{{ $t('insuranceWithdrawal.notFoundTitle') }}</h2>
        <p class="state-text">{{ $t('insuranceWithdrawal.notFoundDescription') }}</p>
      </div>

      <div v-else-if="view === 'throttled'" class="state-wrapper">
        <h2 class="state-title">{{ $t('insuranceWithdrawal.throttledTitle') }}</h2>
        <p class="state-text">{{ $t('insuranceWithdrawal.throttledDescription') }}</p>
      </div>

      <div v-else-if="view === 'networkError'" class="state-wrapper">
        <h2 class="state-title">{{ $t('insuranceWithdrawal.networkErrorTitle') }}</h2>
        <p class="state-text">{{ $t('insuranceWithdrawal.networkErrorDescription') }}</p>
        <main-button depressed @click="retry">
          {{ $t('insuranceWithdrawal.tryAgain') }}
        </main-button>
      </div>

      <div v-else-if="view === 'alreadyReceived'" class="state-wrapper">
        <h2 class="state-title">{{ $t('insuranceWithdrawal.alreadyReceivedTitle') }}</h2>
        <p class="state-text">
          {{
            $t('insuranceWithdrawal.alreadyReceivedDescription', {
              receivedAt: formatDateTime(receivedAt),
            })
          }}
        </p>
      </div>

      <div v-else-if="view === 'success'" class="state-wrapper">
        <h2 class="state-title">{{ $t('insuranceWithdrawal.successTitle') }}</h2>
        <p class="state-text">
          {{
            $t('insuranceWithdrawal.successDescription', {
              receivedAt: formatDateTime(receivedAt),
              reference: reference,
            })
          }}
        </p>
      </div>

      <div v-else class="form-wrapper">
        <div v-if="orderUUID" class="order-info">
          <div class="order-info-row">
            <span class="order-info-label">{{ $t('insuranceWithdrawal.eventLabel') }}</span>
            <span class="order-info-value">{{ eventTitle }}</span>
          </div>
          <div class="order-info-row">
            <span class="order-info-label">{{ $t('insuranceWithdrawal.purchaseDateLabel') }}</span>
            <span class="order-info-value">{{ formatDate(purchasedAt) }}</span>
          </div>
          <div class="order-info-row">
            <span class="order-info-label">{{ $t('insuranceWithdrawal.insuredAmountLabel') }}</span>
            <span class="order-info-value">{{ formattedInsuranceAmount }} €</span>
          </div>
        </div>

        <v-text-field
          v-if="!orderUUID"
          v-model="invoiceNumber"
          :label="$t('insuranceWithdrawal.invoiceNumberLabel')"
          :hint="$t('insuranceWithdrawal.invoiceNumberHint')"
          persistent-hint
          :error-messages="invoiceNumberError"
          required
        />

        <v-text-field
          v-model="email"
          :label="$t('insuranceWithdrawal.emailLabel')"
          :error-messages="emailError"
          type="email"
          required
        />

        <v-textarea
          v-model="message"
          :label="$t('insuranceWithdrawal.messageLabel')"
          rows="3"
        />

        <v-checkbox
          v-model="confirmed"
          :label="$t('insuranceWithdrawal.confirmationLabel')"
          :error-messages="confirmationError"
        />

        <!-- Honeypot field: hidden from real users, left empty by them; a filled value marks a bot -->
        <input
          v-model="website"
          type="text"
          name="website"
          class="honeypot-field"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
        >

        <p v-if="submitError" class="submit-error">{{ submitError }}</p>

        <main-button
          depressed
          :loading="view === 'submitting'"
          :disabled="view === 'submitting'"
          @click="submit"
        >
          {{ view === 'submitting' ? $t('insuranceWithdrawal.submitting') : $t('insuranceWithdrawal.submit') }}
        </main-button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import i18next from 'i18next'
import { getInsuranceWithdrawalOrder, submitInsuranceWithdrawal } from '@/http'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'insurance-withdrawal',
  data() {
    return {
      view: 'form', // loading | form | submitting | success | alreadyReceived | notFound | throttled | networkError
      activeLanguage: 'hr',
      orderUUID: null,
      eventTitle: '',
      purchasedAt: null,
      insuranceAmount: '0',
      invoiceNumber: '',
      email: '',
      message: '',
      confirmed: false,
      website: '',
      receivedAt: null,
      reference: '',
      invoiceNumberError: '',
      emailError: '',
      confirmationError: '',
      submitError: '',
    }
  },
  computed: {
    formattedInsuranceAmount() {
      return Number(this.insuranceAmount || 0).toFixed(2)
    },
  },
  created() {
    const lang = this.$route.query.lang
    this.setLanguage(typeof lang === 'string' && /^en(?:[-_]|$)/i.test(lang) ? 'en' : 'hr')

    this.orderUUID = this.$route.query.order || null

    if (this.orderUUID) {
      this.loadOrder()
    }
  },
  methods: {
    setLanguage(language) {
      this.activeLanguage = language
      i18next.changeLanguage(language)
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    formatDateTime(date) {
      return moment(date).tz('Europe/Zagreb').format('DD.MM.YYYY HH:mm [Zagreb]')
    },
    retry() {
      if (this.orderUUID) {
        this.loadOrder()
      } else {
        this.view = 'form'
      }
    },
    async loadOrder() {
      this.view = 'loading'
      try {
        const { data } = await getInsuranceWithdrawalOrder(this.orderUUID)
        this.eventTitle = data.eventTitle
        this.purchasedAt = data.purchasedAt
        this.insuranceAmount = data.insuranceAmount

        if (data.existingWithdrawal) {
          this.receivedAt = data.existingWithdrawal.receivedAt
          this.view = 'alreadyReceived'
        } else {
          this.view = 'form'
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.view = 'notFound'
        } else if (error.response && error.response.status === 429) {
          this.view = 'throttled'
        } else {
          this.view = 'networkError'
        }
      }
    },
    validate() {
      this.invoiceNumberError = ''
      this.emailError = ''
      this.confirmationError = ''
      let valid = true

      if (!this.orderUUID && !this.invoiceNumber.trim()) {
        this.invoiceNumberError = this.$t('insuranceWithdrawal.invoiceNumberRequired')
        valid = false
      }

      if (!this.email.trim()) {
        this.emailError = this.$t('insuranceWithdrawal.emailRequired')
        valid = false
      } else if (!EMAIL_REGEX.test(this.email.trim())) {
        this.emailError = this.$t('insuranceWithdrawal.emailInvalid')
        valid = false
      }

      if (!this.confirmed) {
        this.confirmationError = this.$t('insuranceWithdrawal.confirmationRequired')
        valid = false
      }

      return valid
    },
    async submit() {
      this.submitError = ''

      if (!this.validate()) {
        return
      }

      this.view = 'submitting'

      const payload = {
        email: this.email.trim(),
        message: this.message.trim() || undefined,
        language: this.activeLanguage,
        website: this.website,
      }

      if (this.orderUUID) {
        payload.orderUUID = this.orderUUID
      } else {
        payload.invoiceNumber = this.invoiceNumber.trim()
      }

      try {
        const { data } = await submitInsuranceWithdrawal(payload)
        this.receivedAt = data.receivedAt
        this.reference = data.reference
        this.view = data.alreadyReceived ? 'alreadyReceived' : 'success'
      } catch (error) {
        const status = error.response && error.response.status

        if (status === 404) {
          // Stay on the form so a mistyped email or invoice number can be corrected.
          this.submitError = this.$t('insuranceWithdrawal.notFoundDescription')
          this.view = 'form'
        } else if (status === 429) {
          this.view = 'throttled'
        } else if (status === 400) {
          this.submitError =
            (error.response.data && error.response.data.message) ||
            this.$t('insuranceWithdrawal.networkErrorDescription')
          this.view = 'form'
        } else {
          this.view = 'networkError'
        }
      }
    },
  },
}
</script>

<style scoped>
.page-wrapper {
  background: #ffffff;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
}

.form-card {
  width: 100%;
  max-width: 560px;
}

.language-switch {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 1rem;
}

.language-switch button {
  border: 1px solid #0277bd;
  border-radius: 4px;
  background: #fff;
  color: #0277bd;
  cursor: pointer;
  font-weight: 700;
  padding: 6px 10px;
}

.language-switch button[aria-pressed='true'] {
  background: #0277bd;
  color: #fff;
}

.page-title {
  color: #333;
  font-family: Raleway;
  font-size: 28px;
  font-weight: 800;
  line-height: 120%;
  margin-bottom: 1rem;
}

.explanation {
  color: #555;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1.5rem;
}

.state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.state-title {
  font-family: Raleway;
  font-weight: 700;
  font-size: 1.5rem;
  margin: 0;
}

.state-text {
  color: #555;
  font-size: 1rem;
  margin: 0;
}

.order-info {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.order-info-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.order-info-label {
  color: #666;
  font-size: 0.85rem;
}

.order-info-value {
  color: #333;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: right;
}

.honeypot-field {
  position: absolute;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.submit-error {
  color: #c62828;
  font-size: 0.875rem;
  margin: 0 0 1rem;
}

@media (max-width: 480px) {
  .page-title {
    font-size: 22px;
  }
}
</style>
